import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { analyticsConfigQuery } from '@/graph/dashboard';
import { proceduresForDay } from '@/graph/procedureDelayReason';
import {
  DEFAULT_FCOTS_GRACE_PERIOD_MINUTES,
  DelayReasonPatientRef,
  FirstCaseInfo,
  firstCaseInfoFor,
  firstCaseProcedureIds,
  resolvePatientProcedure,
} from './procedureDelay';

const SCHEDULE_POLL_MS = 60000;

/**
 * One schedule query for the whole PreOP list, so every card can show a
 * first-case indicator without running the per-patient delay queries.
 *
 * Deliberately has no clock: the returned callback only changes when the
 * schedule or grace period changes, and the "1st Case" → "Delayed" flip is
 * timed by the card itself from `graceEndsAt`. Returns `null` for every
 * patient while disabled.
 */
export function usePreOpFirstCaseStatus({ enabled }: { enabled: boolean }) {
  const today = format(new Date(), 'YYYY-MM-DD');

  const { data: scheduleData } = useQuery(proceduresForDay, {
    variables: { date: today, isCanceled: false },
    skip: !enabled,
    fetchPolicy: 'network-only',
    pollInterval: SCHEDULE_POLL_MS,
  });

  const { data: analyticsConfigData } = useQuery(analyticsConfigQuery, { skip: !enabled });
  const gracePeriodMinutes =
    analyticsConfigData?.analyticsConfiguration?.fcotsGracePeriod ?? DEFAULT_FCOTS_GRACE_PERIOD_MINUTES;

  const firstCaseIds = useMemo(() => firstCaseProcedureIds(scheduleData?.procedures ?? []), [scheduleData?.procedures]);

  return useCallback(
    (patient: DelayReasonPatientRef | null | undefined): FirstCaseInfo | null => {
      if (!enabled) {
        return null;
      }
      return firstCaseInfoFor({ procedure: resolvePatientProcedure(patient), firstCaseIds, gracePeriodMinutes });
    },
    [enabled, firstCaseIds, gracePeriodMinutes]
  );
}
